import { enqueueSnackbar } from 'notistack';
import { all, any, compose, equals, includes, map, uniq } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';

import { hasPermissionByIds } from '@gef-ui/cdu-user/selectors';
import { getMessage } from '@gef-ui/localization/messages';

import { MPU_PROCESSED_TASK } from 'features/common/acl/constants';
import dialog from 'features/common/dialog';
import fetch from 'features/common/fetch';
import { HAS_NO_ACCESS_ERR_MSG } from 'features/views/taskFeedback/constants';
// eslint-disable-next-line import/no-cycle
import taskOverview from 'features/views/taskOverview';
import { completeProgress, handleReloadAndPopUps, setProgress } from 'features/views/taskOverview/actions';
import taskSearch from 'features/views/taskSearch';
import userContext from 'features/views/userContext';

import typeTasks from '../typeTasks';
import { dialogs, INFO_ALREADY_PROCESSED_ERR_MSG } from './constants';

export const runCheckForProgress = (processingValue) => (dispatch) => {
	const checkProgress = () => {
		// fetch loop, getting processing statuses > when done > continue further
		return dispatch(
			fetch.actions.get(fetch.constants.urls.checkFinishAllProcessing, {
				queryParams: processingValue ? { eventId: processingValue } : null,
				onSuccess: [
					(response) => () => {
						const processList = response.body;

						if (!processList) {
							return;
						} else if (processList.state === 'NEW' || processList.state === 'RUNNING') {
							dispatch(setProgress(processList));
						} else {
							// state === DONE
							clearInterval(timer);
							dispatch(completeProgress());
							dispatch(handleReloadAndPopUps(processList));
						}
					},
				],
			}),
		);
	};

	// executing checkProgress and setting 2s interval to start long polling
	const timer = setInterval(checkProgress, 2000);
	checkProgress();
};

export const processInformationInTasksOverview =
	(tasks, markSelectedAllTypesFiltered, excludedTaskIds) => async (dispatch) => {
		const taskIds = tasks.map((task) => task?.id);

		const handleReloadsForManuallySelectedOnly = () => {
			if (window.location.pathname === '/tasks') {
				dispatch(taskOverview.actions.reload());

				const taskTypeIds = compose(
					uniq,
					map((task) => task.taskTypeId),
				)(tasks);

				taskTypeIds.map((item) => dispatch(typeTasks.actions.reloadCurrentPage(item)));
				dispatch(typeTasks.actions.clearSelectTasks());
			}
		};

		dispatch(
			fetch.actions.post(fetch.constants.urls.processTask, {
				body: { taskIds },
				onSuccess: [
					() => () => {
						if (isNilOrEmpty(markSelectedAllTypesFiltered)) {
							// only manually selected - FINISH
							enqueueSnackbar(getMessage('toastr:approveInformationProccessed'), { variant: 'success' });
							// dispatch(typeTasks.actions.clearSelectTasks());
							handleReloadsForManuallySelectedOnly();
						} else {
							// mixed (manually selected + processAll) - CONTINUE WITH BATCH
							dispatch(
								processInformationInTasksOverviewBatch(markSelectedAllTypesFiltered, excludedTaskIds),
							);
						}
					},
				],
				onError: [
					({ failure }) =>
						() => {
							if (includes(HAS_NO_ACCESS_ERR_MSG, failure?.parsedContent?.message)) {
								enqueueSnackbar(getMessage('toastr:cannotProcessInfoTaskUserIsNotResolver'), {
									variant: 'error',
								});
								handleReloadsForManuallySelectedOnly();
							}

							if (failure?.parsedContent?.message === INFO_ALREADY_PROCESSED_ERR_MSG) {
								enqueueSnackbar(getMessage('toastr:infoAlreadyProcessed'), {
									variant: 'error',
								});
								handleReloadsForManuallySelectedOnly();
							}
						},
				],
				shouldReportError: ({ failure }) => {
					return failure.status !== 403;
				},
			}),
		);
	};

export const processInformationInTasksOverviewBatch =
	(markSelectedAllTypesFiltered, excludedTaskIds) => (dispatch, getState) => {
		const contextUser = userContext.selectors.getContextUser(getState());

		dispatch(
			fetch.actions.post(fetch.constants.urls.processAllTask, {
				body: {
					taskTypes: markSelectedAllTypesFiltered,
					excludedTaskIds,
				},
				additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
				onSuccess: [
					(response) => () => {
						if (!response.body) return;
						dispatch(runCheckForProgress(response.body));
					},
				],
			}),
		);
	};

// for taskSearch
export const processInformation = (tasks) => (dispatch) => {
	const taskIds = tasks.map((task) => task?.id);
	return dispatch(
		fetch.actions.post(fetch.constants.urls.processTask, {
			body: { taskIds },
			onSuccess: [
				() => () => {
					enqueueSnackbar(getMessage('toastr:approveInformationProccessed'), { variant: 'success' });
					dispatch(taskSearch.actions.reload());
				},
			],
			onError: [
				({ failure }) =>
					() => {
						if (failure?.status === 403) {
							if (includes(HAS_NO_ACCESS_ERR_MSG, failure?.parsedContent?.message)) {
								enqueueSnackbar(getMessage('toastr:cannotProcessInfoTaskUserIsNotResolver'), {
									variant: 'error',
								});

								if (window.location.pathname === '/task/search') {
									dispatch(taskSearch.actions.reload());
								}
							}

							if (failure?.parsedContent?.message === INFO_ALREADY_PROCESSED_ERR_MSG) {
								enqueueSnackbar(getMessage('toastr:infoAlreadyProcessed'), {
									variant: 'error',
								});

								if (window.location.pathname === '/task/search') {
									dispatch(taskSearch.actions.reload());
								}
							}
						}
					},
			],
			shouldReportError: ({ failure }) => {
				return failure.status !== 403;
			},
		}),
	);
};

export const checkIfProcessable =
	({ items, page, type }) =>
	(dispatch, getState) => {
		dispatch(dialog.actions.close(dialogs.userCanProcessMax10Tasks));
		dispatch(dialog.actions.close(dialogs.processedTasks));
		dispatch(dialog.actions.close(dialogs.userDoesntHavePermissions));

		const hasPermissionToProcessTask = hasPermissionByIds(getState())(MPU_PROCESSED_TASK);
		const itemsUserOrSubordOfUserIsSolverValue = items.map(
			(item) => item?.solverIsLoggedUser || item?.solverIsSubord2LoggedUser,
		);

		const hasMoreThanTenItems = items.length > 10;
		const processedTask = items.find((task) => task && task.processed);
		const hasProcessedTasks = !isNilOrEmpty(processedTask);
		const userHasPermissionToProcess =
			all(equals(true))(itemsUserOrSubordOfUserIsSolverValue) || hasPermissionToProcessTask;

		if (page === 'taskSearch') {
			if (type === 'process') {
				hasMoreThanTenItems && dispatch(dialog.actions.open(dialogs.userCanProcessMax10Tasks));
				hasProcessedTasks && dispatch(dialog.actions.open(dialogs.processedTasks));
				!userHasPermissionToProcess && dispatch(dialog.actions.open(dialogs.userDoesntHavePermissions));

				return !any(equals(true))([hasMoreThanTenItems, hasProcessedTasks, !userHasPermissionToProcess]);
			}

			if (type === 'forward') {
				hasMoreThanTenItems && dispatch(dialog.actions.open(dialogs.userCanProcessMax10Tasks));
				hasProcessedTasks && dispatch(dialog.actions.open(dialogs.processedTasks));

				return !any(equals(true))([hasMoreThanTenItems, hasProcessedTasks]);
			}
		}

		if (page === 'tasksOverview') {
			if (type === 'process') {
				!userHasPermissionToProcess && dispatch(dialog.actions.open(dialogs.userDoesntHavePermissions));
				return userHasPermissionToProcess;
			}

			if (type === 'forward') {
				hasMoreThanTenItems && dispatch(dialog.actions.open(dialogs.userCanProcessMax10Tasks));
				return !hasMoreThanTenItems;
			}
		}
	};
